import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ShoppingBag } from "lucide-react";
import { NAV, LOGO, CONTACT } from "@/data/site";
import { useLeadModal } from "@/context/LeadModalProvider";
import { getSavedCart, onCartChange } from "@/lib/savedCart";

export const Navbar = () => {
  const { openBooking, openQuote, resumeBooking } = useLeadModal();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const [cart, setCart] = useState(null);

  useEffect(() => {
    const update = () => setCart(getSavedCart());
    update();
    return onCartChange(update);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobile(false); setSvcOpen(false); }, [pathname]);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/80 backdrop-blur-xl border-b border-border shadow-[0_2px_20px_rgba(27,42,91,0.05)]" : "bg-transparent"}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <img src={LOGO} alt="CJB Clean logo" className="h-14 w-auto object-contain" />
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-bold text-navy tracking-tight">CJB Clean</span>
              <span className="text-[0.65rem] uppercase tracking-[0.18em] text-leaf font-medium mt-0.5">Green Cleaning · DMV</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-7 whitespace-nowrap">
            {NAV.map((n) => n.children ? (
              <div key={n.label} className="relative" onMouseEnter={() => setSvcOpen(true)} onMouseLeave={() => setSvcOpen(false)}>
                <Link to={n.to} data-testid="nav-link-services"
                  className={`text-sm font-medium tracking-wide transition-colors flex items-center gap-1 ${pathname.startsWith("/services") ? "text-leaf" : "text-navy hover:text-leaf"}`}>
                  {n.label} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${svcOpen ? "rotate-180" : ""}`} />
                </Link>
                {svcOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3" data-testid="services-dropdown">
                    <div className="bg-white rounded-2xl border border-border shadow-xl p-2 w-60">
                      {n.children.map((c) => (
                        <Link key={c.to + c.label} to={c.to} data-testid={`nav-dropdown-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className="block px-4 py-2.5 rounded-xl text-sm font-medium text-navy hover:bg-muted transition-colors">{c.label}</Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link key={n.to} to={n.to} data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm font-medium tracking-wide transition-colors relative group ${pathname === n.to ? "text-leaf" : "text-navy hover:text-leaf"}`}>
                {n.label}
                <span className={`absolute -bottom-1.5 left-0 h-0.5 bg-leaf transition-all duration-300 ${pathname === n.to ? "w-full" : "w-0 group-hover:w-full"}`} />
              </Link>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-3 whitespace-nowrap">
            {cart && (
              <button onClick={resumeBooking} data-testid="nav-cart-btn" title="Resume your booking"
                className="relative flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium text-navy bg-leaf/10 border border-leaf/30 hover:bg-leaf/20 transition-colors">
                <ShoppingBag className="w-4 h-4 text-leaf" />
                <span>Resume{cart.estimate?.low ? ` · $${cart.estimate.low}` : ""}</span>
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-leaf border-2 border-white" />
              </button>
            )}
            <button onClick={openQuote} data-testid="nav-quote-btn"
              className="px-5 py-2.5 rounded-full text-sm font-medium text-navy border border-navy/20 hover:border-navy hover:bg-navy hover:text-white transition-all duration-200">
              Get Instant Quote
            </button>
            <button onClick={openBooking} data-testid="nav-book-btn"
              className="px-5 py-2.5 rounded-full text-sm font-medium bg-leaf text-white hover:scale-105 transition-transform duration-200">
              Book Online
            </button>
          </div>

          <div className="xl:hidden flex items-center gap-2">
            {cart && (
              <button onClick={resumeBooking} data-testid="nav-cart-btn-mobile" aria-label="Resume your booking"
                className="relative w-10 h-10 rounded-full bg-leaf/10 border border-leaf/30 flex items-center justify-center text-leaf">
                <ShoppingBag className="w-5 h-5" />
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-leaf border-2 border-white" />
              </button>
            )}
            <button className="text-navy" onClick={() => setMobile(!mobile)} data-testid="nav-mobile-toggle">
              {mobile ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      {mobile && (
        <div className="xl:hidden bg-white/95 backdrop-blur-xl border-t border-border px-5 py-6 space-y-3 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <Link to="/" className="block text-lg font-medium text-navy" data-testid="mobile-link-home">Home</Link>
          <Link to="/services" className="block text-lg font-medium text-navy" data-testid="mobile-link-services">Services</Link>
          <div className="pl-4 space-y-2">
            {NAV[1].children.slice(1).map((c) => (
              <Link key={c.to} to={c.to} className="block text-sm text-muted-foreground">{c.label}</Link>
            ))}
          </div>
          <Link to="/service-areas" className="block text-lg font-medium text-navy" data-testid="mobile-link-service-areas">Service Areas</Link>
          <Link to="/our-story" className="block text-lg font-medium text-navy" data-testid="mobile-link-our-story">Our Story</Link>
          <Link to="/capability-statement" className="block text-lg font-medium text-navy" data-testid="mobile-link-capability">Capability Statement</Link>
          <Link to="/contact" className="block text-lg font-medium text-navy" data-testid="mobile-link-contact">Contact</Link>
          <div className="flex flex-col gap-3 pt-3">
            <button onClick={openQuote} data-testid="mobile-quote-btn" className="w-full text-center py-3 rounded-full border border-navy/20 text-navy font-medium">Get Instant Quote</button>
            <button onClick={openBooking} data-testid="mobile-book-btn" className="w-full py-3 rounded-full bg-leaf text-white font-medium">Book Online</button>
          </div>
        </div>
      )}
    </header>
  );
};
